/**
 * Product Personalization / Engraving Module
 * LittleDeluxe - Shopify Theme Component
 *
 * Web Component: <product-personalization>
 * Handles: single text input with auto-split at 12 chars,
 *          font selection, icon insertion (special chars),
 *          live SVG preview, LocalStorage, form integration.
 *
 * Icons insert special characters that the engraving machine
 * interprets as icons (e.g., "(" = smiley, "^" = heart, etc.).
 */

if (!customElements.get('product-personalization')) {

  const _STORAGE_KEY = 'ld_engraving';
  const _STORAGE_EXPIRY = 48 * 60 * 60 * 1000;
  const _ICON_CHARS = new Set(['/', '(', ')', '<', '>', '^', '[', ']']);
  const _SYMBOLER_FONTS = ['LD-Standard', 'LD-ComicSans', 'LD-Dancing', 'LD-Arial'];
  const _SAFE_FONT = 'Arial, Helvetica, sans-serif';

  class ProductPersonalization extends HTMLElement {

    constructor() {
      super();
      this._initialized = false;
      this._previewOverlay = null;
      this._resizeObserver = null;
      this._mutationObserver = null;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      // Read config from data attributes
      this.sectionId = this.dataset.sectionId || '';
      this.productFormId = this.dataset.productFormId || '';
      this.curveHandle = this.dataset.curveHandle || 'round';
      this.maxChars = parseInt(this.dataset.maxChars, 10) || 12;
      this.allowSecondLine = this.dataset.allowSecondLine !== 'false';
      this.engravingRequired = this.dataset.engravingRequired === 'true';

      // ─── Read simplified curve config from Metaobject data attributes ───
      const _py = parseInt(this.dataset.positionY, 10);
      const _cb = parseInt(this.dataset.curveBend, 10);
      const _tw = parseInt(this.dataset.textWidth, 10);
      const _fs = parseInt(this.dataset.fontSize, 10);
      const _lo = parseInt(this.dataset.line2Offset, 10);
      const positionY = isNaN(_py) ? 45 : _py;
      const curveBend = isNaN(_cb) ? 28 : _cb;
      const textWidth = isNaN(_tw) ? 110 : _tw;
      const fontSize  = isNaN(_fs) ? 14 : _fs;
      const line2Off  = isNaN(_lo) ? 16 : _lo;

      // Store raw curve parameters for building paths
      this.curveConfig = {
        positionY, curveBend, textWidth,
        centerX: 75,
        fontSize, line2Offset: line2Off,
        isCurved: curveBend > 0
      };

      // Track loaded fonts
      this._loadedFonts = new Set();

      // Cache DOM references
      this._cacheElements();

      // Preload all Google Fonts
      this._preloadAllFonts();

      // Setup event listeners
      this._bindEvents();

      // Inject hidden form inputs
      this._injectHiddenInputs();

      // Restore from LocalStorage
      this._restoreFromStorage();

      // Build custom font dropdown (shows each name in its own font)
      this._buildFontDropdown();

      // Apply initial font to text input
      this._applyFontToInput();

      // Initialize preview
      this._initPreview();

      // Mark as initialized for CSS animations
      this.querySelector('.personalization')?.setAttribute('data-initialized', '');

      // Watch for variant changes re-enabling the buy button
      this._watchBuyButton();
    }

    disconnectedCallback() {
      this._resizeObserver?.disconnect();
      this._mutationObserver?.disconnect();
      this._slideObserver?.disconnect();
      if (this._buyBtnObservers) this._buyBtnObservers.forEach(o => o.disconnect());
      if (this._slideCheckInterval) clearInterval(this._slideCheckInterval);
      window.removeEventListener('resize', this._handleResizeBound);
    }

    // ─── DOM caching ───

    _cacheElements() {
      this.toggle = this.querySelector('[data-toggle]');
      this.body = this.querySelector('[data-body]');
      this.textInput = this.querySelector('[data-engraving-text]');
      this.fontSelect = this.querySelector('[data-font-select]');
      this.iconButtons = this.querySelectorAll('[data-icon-btn]');
      this.clearBtn = this.querySelector('[data-clear]');
      this.charCount = this.querySelector('[data-char-count]');
      this.toggleLabel = this.querySelector('[data-toggle-label]');
      this.lineIndicator = this.querySelector('[data-line-indicator]');
      this.line1Preview = this.querySelector('[data-line1-preview]');
      this.line2Preview = this.querySelector('[data-line2-preview]');

      // Engraving text color from backend theme setting
      this.textColor = this.dataset.textColor || '#313131';
    }

    // ─── Event binding ───

    _bindEvents() {
      // Toggle engraving on/off
      this.toggle?.addEventListener('change', (e) => this._handleToggle(e));

      // Single text input
      this.textInput?.addEventListener('input', () => this._handleTextInput());

      // Font selector
      this.fontSelect?.addEventListener('change', (e) => this._handleFontChange(e));

      // Icon buttons
      this.iconButtons.forEach(btn => {
        btn.addEventListener('click', (e) => this._handleIconClick(e));
      });

      // Clear button
      this.clearBtn?.addEventListener('click', () => this._handleClear());

      // Resize handler
      this._handleResizeBound = this._debounce(() => this._repositionPreview(), 150);
      window.addEventListener('resize', this._handleResizeBound);
    }

    // ─── Helper: split text into two lines at maxChars ───

    _splitText(fullText) {
      const line1 = fullText.substring(0, this.maxChars);
      const line2 = fullText.substring(this.maxChars, this.maxChars * 2);
      return { line1, line2 };
    }

    // ─── Toggle handler ───

    _handleToggle(e) {
      const isEnabled = e.target.checked;
      if (this.body) {
        this.body.classList.toggle('personalization__body--open', isEnabled);
      }
      const toggleWrapper = this.querySelector('.personalization__toggle');
      if (toggleWrapper) {
        toggleWrapper.classList.toggle('personalization__toggle--active', isEnabled);
      }
      if (this.toggleLabel) {
        this.toggleLabel.textContent = isEnabled
          ? (this.dataset.i18nToggleActive || 'Gravering aktiv')
          : (this.dataset.i18nToggleAdd || 'Tilføj gravering');
      }

      if (isEnabled) {
        this._syncHiddenInputs();
        if (!this._previewOverlay) {
          this._initPreview();
        } else {
          this._updatePreview();
          this._showPreview();
        }
      } else {
        this._clearHiddenInputs();
        this._hidePreview();
      }

      this._updateBuyButton();
      this._saveToStorage();
    }

    // ─── Text input handler (single field, auto-split) ───

    _handleTextInput() {
      const input = this.textInput;
      if (!input) return;

      let value = input.value;
      const totalMax = this.maxChars * 2; // 24

      // Enforce total max
      if (value.length > totalMax) {
        value = value.substring(0, totalMax);
        input.value = value;
      }

      // Split into lines
      const { line1, line2 } = this._splitText(value);

      // Update counter – show which line we're on
      if (this.charCount) {
        if (value.length <= this.maxChars) {
          // Still on line 1
          this.charCount.textContent = `${value.length}/${this.maxChars}`;
          this.charCount.classList.toggle('personalization__char-count--max', value.length >= this.maxChars);
        } else {
          // On line 2
          this.charCount.textContent = `${this.maxChars}/${this.maxChars} + ${line2.length}/${this.maxChars}`;
          this.charCount.classList.toggle('personalization__char-count--max', value.length >= totalMax);
        }
      }

      // Show line split indicator when text exceeds line 1
      this._updateLineIndicator(line1, line2);

      // Sync & update
      this._syncHiddenInputs();
      this._updatePreview();
      this._updateBuyButton();
      this._saveToStorage();
    }

    // ─── Line indicator update (extracted so font change can also refresh it) ───

    _updateLineIndicator(line1, line2) {
      if (!this.lineIndicator) return;
      // If no args passed, recompute from current text
      if (line1 === undefined) {
        const fullText = this.textInput?.value || '';
        const split = this._splitText(fullText);
        line1 = split.line1;
        line2 = split.line2;
      }
      if (line2.length > 0) {
        this.lineIndicator.style.display = '';
        const cssStack = this._getSelectedCssStack();
        if (this.line1Preview) {
          this.line1Preview.innerHTML = `<strong>Linje 1:</strong> ${this._buildHtmlText(line1, cssStack)}`;
        }
        if (this.line2Preview) {
          this.line2Preview.innerHTML = `Linje 2: ${this._buildHtmlText(line2, cssStack)}`;
        }
      } else {
        this.lineIndicator.style.display = 'none';
      }
    }

    _applyFontToInput() {
      if (!this.fontSelect || !this.textInput) return;
      const selectedOption = this.fontSelect.options[this.fontSelect.selectedIndex];
      const cssStack = selectedOption?.dataset.cssStack || '';
      if (!cssStack) return;
      this.textInput.style.fontFamily = cssStack;
    }

    // ─── Helper: get current CSS stack ───
    _getSelectedCssStack() {
      const opt = this.fontSelect?.options[this.fontSelect?.selectedIndex];
      return opt?.dataset.cssStack || '';
    }

    // ─── Helper: escape HTML ───
    _escHtml(str) {
      return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    // ─── Helper: escape for CSS attribute ───
    _escAttr(str) {
      return str.replace(/"/g, '\\"');
    }

    // ─── Icon characters that need SYMBOLER font fallback ───
    // ICON_CHARS and SYMBOLER_FONTS defined as module-level constants above

    _fontHasIcons(cssStack) {
      return _SYMBOLER_FONTS.some(f => cssStack.includes(f));
    }

    // Build HTML: only icon-list chars use SYMBOLER font; all others use safe font
    // (SYMBOLER fonts map ; : etc. to symbols – we only want icons from the icon list)
    _buildHtmlText(text, cssStack) {
      const iconFallback = "'LD-ComicSans','Comic Sans MS',cursive";
      const textFont = cssStack || _SAFE_FONT;
      let out = '';
      let buf = '';
      for (const ch of text) {
        if (_ICON_CHARS.has(ch)) {
          if (buf) { out += `<span style="font-family:${this._escAttr(textFont)}">${this._escHtml(buf)}</span>`; buf = ''; }
          out += `<span style="font-family:${iconFallback}">${this._escHtml(ch)}</span>`;
        } else {
          buf += ch;
        }
      }
      if (buf) out += `<span style="font-family:${this._escAttr(textFont)}">${this._escHtml(buf)}</span>`;
      return out;
    }

    // ─── Dynamic font size calculation ───
    // First letter is larger; text shrinks as more characters are added.

    _calcDynamicFontSize(textLength, baseFontSize) {
      const base = baseFontSize || this.curveConfig.fontSize;
      if (textLength <= 1) return base * 1.35;
      if (textLength <= 3) return base * 1.2;
      if (textLength <= 6) return base * 1.1;
      if (textLength <= 9) return base;
      if (textLength <= 12) return base * 0.9;
      return base * 0.8;
    }

    // ─── Character width measurement using Canvas 2D ───

    _measureCharWidths(text, fontSize, fontFamily) {
      if (!this._measureCtx) {
        this._measureCtx = document.createElement('canvas').getContext('2d');
      }
      const REF = 100;
      this._measureCtx.font = `500 ${REF}px ${fontFamily || 'sans-serif'}`;
      const scale = fontSize / REF;
      return [...text].map(ch => this._measureCtx.measureText(ch).width * scale);
    }

    // Measure with per-char font: SYMBOLER font only for icon chars, safe font for rest
    _measureCharWidthsForDisplay(text, fontSize, cssStack) {
      if (!this._measureCtx) {
        this._measureCtx = document.createElement('canvas').getContext('2d');
      }
      const REF = 100;
      const scale = fontSize / REF;
      return [...text].map(ch => {
        const font = (_ICON_CHARS.has(ch) && this._fontHasIcons(cssStack)) ? cssStack : (cssStack || _SAFE_FONT);
        this._measureCtx.font = `500 ${REF}px ${font}`;
        return this._measureCtx.measureText(ch).width * scale;
      });
    }

    // ─── Straight (horizontal) text placement ───

    _placeCharsStraight(text, fontSize, kerning, centerX, centerY, lineOffset) {
      if (!text || text.length === 0) return [];

      const cssStack = this._getSelectedCssStack();
      const charWidths = this._measureCharWidthsForDisplay(text, fontSize, cssStack);
      const totalW = charWidths.reduce((s, w) => s + w, 0) + (text.length - 1) * kerning;
      let x = centerX - totalW / 2;
      const y = centerY + lineOffset;
      const chars = [];

      for (let i = 0; i < text.length; i++) {
        x += charWidths[i] / 2;
        chars.push({
          char: text[i],
          x: Math.round(x * 100) / 100,
          y: Math.round(y * 100) / 100,
          rotation: 0,
          isIcon: _ICON_CHARS.has(text[i])
        });
        x += charWidths[i] / 2 + kerning;
      }
      return chars;
    }

    // ─── Circular arc text placement ───

    _placeCharsOnArc(text, fontSize, diameter, kerning, centerX, arcCenterY, lineOffset) {
      if (!text || text.length === 0) return [];

      const R = diameter / 2;
      const spacingR = R - fontSize;
      const baseR = R - fontSize / 2;
      const posR = baseR - lineOffset;
      if (spacingR <= 0 || posR <= 0) return [];

      const cssStack = this._getSelectedCssStack();
      const charWidths = this._measureCharWidthsForDisplay(text, fontSize, cssStack);

      let totalAngle = 0;
      for (let i = 0; i < text.length; i++) {
        totalAngle += charWidths[i] / spacingR;
        if (i < text.length - 1) totalAngle += kerning / spacingR;
      }

      let angle = -totalAngle / 2;
      const chars = [];

      for (let i = 0; i < text.length; i++) {
        const halfCharAngle = (charWidths[i] / 2) / spacingR;
        angle += halfCharAngle;

        const x = centerX + posR * Math.sin(angle);
        const y = arcCenterY - posR * Math.cos(angle);
        const rotDeg = angle * (180 / Math.PI);

        chars.push({
          char: text[i],
          x: Math.round(x * 100) / 100,
          y: Math.round(y * 100) / 100,
          rotation: Math.round(rotDeg * 100) / 100,
          isIcon: _ICON_CHARS.has(text[i])
        });

        angle += (charWidths[i] / 2 + kerning) / spacingR;
      }

      return chars;
    }

    // ─── Font change handler ───

    _handleFontChange(e) {
      const selectedOption = e.target.options[e.target.selectedIndex];
      const fontUrl = selectedOption?.dataset.fontUrl || '';
      const cssStack = selectedOption?.dataset.cssStack || '';

      // Load the Google Font if it has a URL
      this._loadGoogleFont(fontUrl);

      // Apply font to the text input so icons render while typing
      if (this.textInput && cssStack) {
        this.textInput.style.fontFamily = cssStack;
      }

      // Update custom font dropdown display
      this._updateFontDropdownDisplay();

      // Sync & update
      this._syncHiddenInputs();
      this._updatePreview();
      this._updateLineIndicator();   // Re-render line indicator with new font
      this._saveToStorage();
    }

    // ─── Google Fonts loader ───

    _loadGoogleFont(url) {
      if (!url || this._loadedFonts.has(url)) return;
      this._loadedFonts.add(url);

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }

    _preloadAllFonts() {
      if (!this.fontSelect) return;
      for (let i = 0; i < this.fontSelect.options.length; i++) {
        const fontUrl = this.fontSelect.options[i].dataset.fontUrl;
        if (fontUrl) this._loadGoogleFont(fontUrl);
      }
    }

    // ─── Custom font dropdown (shows each font name in its own typeface) ───

    _buildFontDropdown() {
      if (!this.fontSelect) return;

      const container = this.fontSelect.closest('.personalization__font-selector');
      if (!container) return;

      const dropdown = document.createElement('div');
      dropdown.className = 'personalization__font-dropdown';
      dropdown.setAttribute('translate', 'no');

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'personalization__font-dropdown-trigger';

      const list = document.createElement('ul');
      list.className = 'personalization__font-dropdown-list';

      for (let i = 0; i < this.fontSelect.options.length; i++) {
        const opt = this.fontSelect.options[i];
        const li = document.createElement('li');
        li.className = 'personalization__font-dropdown-item';
        li.textContent = opt.textContent.trim();
        li.dataset.index = i;
        li.style.fontFamily = opt.dataset.cssStack || '';
        list.appendChild(li);
      }

      dropdown.appendChild(trigger);
      dropdown.appendChild(list);
      container.appendChild(dropdown);

      this._fontDropdown = dropdown;
      this._fontDropdownLabel = trigger;
      this._fontDropdownList = list;

      this._updateFontDropdownDisplay();

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        dropdown.classList.toggle('personalization__font-dropdown--open');
      });

      list.addEventListener('click', (e) => {
        const item = e.target.closest('.personalization__font-dropdown-item');
        if (!item) return;
        const idx = parseInt(item.dataset.index, 10);
        this.fontSelect.selectedIndex = idx;
        this.fontSelect.dispatchEvent(new Event('change', { bubbles: true }));
        dropdown.classList.remove('personalization__font-dropdown--open');
      });

      document.addEventListener('click', (e) => {
        if (!dropdown.contains(e.target)) {
          dropdown.classList.remove('personalization__font-dropdown--open');
        }
      });
    }

    _updateFontDropdownDisplay() {
      if (!this.fontSelect || !this._fontDropdownLabel) return;
      const opt = this.fontSelect.options[this.fontSelect.selectedIndex];
      if (!opt) return;
      this._fontDropdownLabel.textContent = opt.textContent.trim();
      this._fontDropdownLabel.style.fontFamily = opt.dataset.cssStack || '';
      this._fontDropdownLabel.title = opt.textContent.trim();

      const items = this._fontDropdownList?.querySelectorAll('.personalization__font-dropdown-item');
      items?.forEach((item) => {
        const isActive = parseInt(item.dataset.index, 10) === this.fontSelect.selectedIndex;
        item.classList.toggle('personalization__font-dropdown-item--active', isActive);
      });
    }

    // ─── Icon click handler ───
    // Icons insert a SPECIAL CHARACTER (not emoji) into the text field.
    // The engraving machine maps these characters to physical icons.

    _handleIconClick(e) {
      const btn = e.currentTarget;
      const specialChar = btn.dataset.specialChar;
      if (!specialChar || !this.textInput) return;

      const currentValue = this.textInput.value;
      const totalMax = this.maxChars * 2;
      if (currentValue.length + specialChar.length > totalMax) return;

      // Insert at cursor position
      const start = this.textInput.selectionStart || currentValue.length;
      const end = this.textInput.selectionEnd || currentValue.length;
      const newValue = currentValue.substring(0, start) + specialChar + currentValue.substring(end);
      this.textInput.value = newValue;

      // Move cursor after the inserted character
      const newPos = start + specialChar.length;
      this.textInput.setSelectionRange(newPos, newPos);
      this.textInput.focus();

      // Trigger input event
      this._handleTextInput();
    }

    // ─── Clear handler ───

    _handleClear() {
      if (this.textInput) {
        this.textInput.value = '';
        this._handleTextInput();
      }
      if (this.fontSelect) {
        this.fontSelect.selectedIndex = 0;
        this.fontSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // ─── Hidden form inputs ───
    // These are submitted as Shopify line item properties.
    // The special characters from icons are included in the text
    // and will appear on the order/packing slip as-is.

    _injectHiddenInputs() {
      const form = document.getElementById(this.productFormId);
      if (!form) return;

      const fields = [
        { name: 'properties[Gravering]',        id: `engraving-text-${this.sectionId}` },
        { name: 'properties[Skrifttype]',       id: `engraving-font-${this.sectionId}` },
        { name: 'properties[_engraving_curve]', id: `engraving-curve-${this.sectionId}` },
        { name: 'properties[_tekstfarve]',      id: `engraving-color-${this.sectionId}` }
      ];

      fields.forEach(({ name, id }) => {
        if (form.querySelector(`#${id}`)) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.id = id;
        input.value = '';
        // Do NOT set the `name` attribute here. _syncHiddenInputs() attaches the
        // name only once the customer actually enters engraving text. This prevents
        // empty line item properties from being saved on orders where no engraving
        // was chosen (otherwise every add-to-cart submitted blank Engraving/Font/etc).
        input.dataset.propName = name;
        form.appendChild(input);
      });
    }

    _syncHiddenInputs() {
      const textEl = document.getElementById(`engraving-text-${this.sectionId}`);
      const fontEl = document.getElementById(`engraving-font-${this.sectionId}`);
      const curveEl = document.getElementById(`engraving-curve-${this.sectionId}`);
      const colorEl = document.getElementById(`engraving-color-${this.sectionId}`);

      const fullText = this.textInput?.value || '';
      const { line1, line2 } = this._splitText(fullText);
      const combinedText = (line1.trim() + (line2.trim() ? ' ' + line2.trim() : '')).trim();
      const fontName = this.fontSelect?.options[this.fontSelect?.selectedIndex]?.text || '';

      if (textEl) textEl.value = combinedText;
      if (fontEl) fontEl.value = fontName;
      if (curveEl) curveEl.value = this.curveHandle;
      if (colorEl) colorEl.value = this.textColor || '#313131';

      if (textEl && !combinedText) textEl.removeAttribute('name');
      else if (textEl) textEl.setAttribute('name', 'properties[Gravering]');

      if (fontEl && !combinedText) fontEl.removeAttribute('name');
      else if (fontEl) fontEl.setAttribute('name', 'properties[Skrifttype]');

      if (curveEl && !combinedText) curveEl.removeAttribute('name');
      else if (curveEl) curveEl.setAttribute('name', 'properties[_engraving_curve]');

      if (colorEl && !combinedText) colorEl.removeAttribute('name');
      else if (colorEl) colorEl.setAttribute('name', 'properties[_tekstfarve]');
    }

    _clearHiddenInputs() {
      ['text', 'font', 'curve', 'color'].forEach(key => {
        const el = document.getElementById(`engraving-${key}-${this.sectionId}`);
        if (el) {
          el.value = '';
          el.removeAttribute('name');
        }
      });
    }

    // ─── Buy button control ───

    _getAllBuyButtons() {
      const buttons = [];
      const form = document.getElementById(this.productFormId);
      if (form) {
        const btn = form.querySelector('[name="add"]');
        if (btn) buttons.push(btn);
      }
      document.querySelectorAll('sticky-atc-bar [name="add"], sticky-atc-bar-minimalist [name="add"]').forEach(btn => {
        if (!buttons.includes(btn)) buttons.push(btn);
      });
      return buttons;
    }

    _watchBuyButton() {
      this._buyBtnObservers = [];
      const buttons = this._getAllBuyButtons();

      buttons.forEach(submitBtn => {
        const obs = new MutationObserver(() => {
          const isEngravingOn = this.toggle?.checked;
          const hasText = (this.textInput?.value?.trim() || '').length > 0;
          if (isEngravingOn && !hasText && !submitBtn.hasAttribute('disabled')) {
            submitBtn.setAttribute('disabled', '');
            const btnText = submitBtn.querySelector('.btn__text') || submitBtn.querySelector('span');
            if (btnText) btnText.textContent = this.dataset.i18nSubmitPrompt || 'Skriv din graveringstekst';
          }
        });
        obs.observe(submitBtn, { attributes: true, attributeFilter: ['disabled'] });
        this._buyBtnObservers.push(obs);
      });
    }

    _updateBuyButton() {
      const isEngravingOn = this.toggle?.checked;
      const hasText = (this.textInput?.value?.trim() || '').length > 0;
      const buttons = this._getAllBuyButtons();

      buttons.forEach(submitBtn => {
        if (isEngravingOn && !hasText) {
          submitBtn.setAttribute('disabled', '');
          const btnText = submitBtn.querySelector('.btn__text') || submitBtn.querySelector('span');
          if (btnText) btnText.textContent = this.dataset.i18nSubmitPrompt || 'Skriv din graveringstekst';
        } else {
          const form = submitBtn.closest('form');
          const variantInput = form ? form.querySelector('input[name="id"], select[name="id"]') : null;
          const isDisabledByVariant = variantInput && variantInput.disabled;
          if (!isDisabledByVariant) {
            submitBtn.removeAttribute('disabled');
            const btnText = submitBtn.querySelector('.btn__text') || submitBtn.querySelector('span');
            if (btnText && typeof FoxTheme !== 'undefined' && FoxTheme.variantStrings?.addToCart) {
              btnText.textContent = FoxTheme.variantStrings.addToCart;
            } else if (btnText) {
              btnText.textContent = 'Læg i kurv';
            }
          }
        }
      });
    }

    // ─── Live SVG Preview ───

    _initPreview() {
      const productSection = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!productSection) return;

      // Place overlay on the gallery viewer container (not individual slides)
      const galleryViewer = productSection.querySelector(`#GalleryViewer-${this.sectionId}`);
      if (!galleryViewer) {
        if (!this._initRetries) this._initRetries = 0;
        this._initRetries++;
        if (this._initRetries < 10) {
          setTimeout(() => this._initPreview(), 500);
        }
        return;
      }

      this._galleryViewer = galleryViewer;

      // Add overlay directly on the gallery viewer container
      if (!galleryViewer.querySelector('.personalization-preview-overlay')) {
        if (getComputedStyle(galleryViewer).position === 'static') {
          galleryViewer.style.position = 'relative';
        }
        const overlay = document.createElement('div');
        overlay.className = 'personalization-preview-overlay';
        overlay.innerHTML = this._buildPreviewSVG();
        overlay.style.cssText = 'display:none;position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:10;overflow:visible;';
        galleryViewer.appendChild(overlay);
      }

      this._previewOverlay = galleryViewer.querySelector('.personalization-preview-overlay');

      // Listen for slide changes to show overlay only on first slide
      this._watchSlideChanges(productSection);

      // If toggle is on and has text, update and show
      if (this.toggle?.checked && this.textInput?.value?.trim()) {
        this._updatePreview();
        this._showPreview();
      }
    }

    _getSwiper() {
      const galleryEl = document.querySelector(`#MediaGallery-${this.sectionId}`);
      if (galleryEl && galleryEl.sliderInstance && galleryEl.sliderInstance.slider) {
        return galleryEl.sliderInstance.slider;
      }
      return null;
    }

    _isOnFirstSlide() {
      const swiper = this._getSwiper();
      if (swiper) return swiper.realIndex === 0;
      return true;
    }

    _watchSlideChanges() {
      const self = this;

      const checkSlide = () => {
        if (!self.toggle?.checked || !self.textInput?.value?.trim()) return;

        // Always get the CURRENT overlay from DOM (variant app may recreate gallery)
        const section = document.getElementById(`MainProduct-${self.sectionId}`);
        if (!section) return;

        let overlay = section.querySelector('.personalization-preview-overlay');

        // If overlay was lost (gallery recreated), create a new one
        if (!overlay) {
          const viewer = section.querySelector(`#GalleryViewer-${self.sectionId}`);
          if (!viewer) return;
          if (getComputedStyle(viewer).position === 'static') viewer.style.position = 'relative';
          overlay = document.createElement('div');
          overlay.className = 'personalization-preview-overlay';
          overlay.innerHTML = self._buildPreviewSVG();
          overlay.style.cssText = 'display:block;position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:10;overflow:visible;';
          viewer.appendChild(overlay);
          self._previewOverlay = overlay;
          self._updatePreview();
        }

        // Update cached reference
        self._previewOverlay = overlay;

        const shouldShow = self._isOnFirstSlide();
        overlay.style.display = shouldShow ? 'block' : 'none';
      };

      this._slideCheckInterval = setInterval(checkSlide, 250);
    }

    _buildPreviewSVG() {
      return `
        <svg viewBox="0 0 150 100" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg"
             style="width:100%;height:100%;display:block;">
          <g data-arc-line1></g>
          <g data-arc-line2></g>
        </svg>
      `;
    }

    _computeEffectiveParams(hasLine2) {
      const PRESETS = {
        round: {
          positionY: 20.5, centerX: 77, diameter: 71, fontSize: 9,
          letterSpacing: 1, line2Offset: 4.5, fontWeight: 400, opacity: 0.8,
          maxTextWidth: 43,
          twoLine: { diamMul: 1, l2offMul: 0.9 }
        },
        butterfly: {
          positionY: 40.5, centerX: 76, diameter: 300, fontSize: 8.5,
          letterSpacing: 0.4, line2Offset: 3.5, fontWeight: 400, opacity: 0.55,
          straight: true, maxTextWidth: 43,
          twoLine: { diamMul: 2, l2offMul: 1 }
        },
        flat: {
          positionY: 45, centerX: 74, diameter: 300, fontSize: 8.5,
          letterSpacing: 0.4, line2Offset: 3, fontWeight: 400, opacity: 0.55,
          straight: true, maxTextWidth: 45,
          twoLine: { diamMul: 2, l2offMul: 1 }
        },
        lucky: {
          positionY: 45, centerX: 74, diameter: 300, fontSize: 8.5,
          letterSpacing: 0.4, line2Offset: 3, fontWeight: 400, opacity: 0.55,
          straight: true, maxTextWidth: 45,
          twoLine: { diamMul: 2, l2offMul: 1 }
        }
      };
      const preset = PRESETS[this.curveHandle] || PRESETS.round;

      let baseFontSize = preset.fontSize;
      let diameter = preset.diameter;
      let letterSpacing = preset.letterSpacing;
      let nextLineOffset = preset.line2Offset;
      let centerX = preset.centerX;
      let centerY = preset.positionY;

      if (hasLine2) {
        diameter *= preset.twoLine.diamMul;
        nextLineOffset *= preset.twoLine.l2offMul;
      }

      return {
        baseFontSize, diameter, kerning: letterSpacing, nextLineOffset,
        centerX, centerY, fontWeight: preset.fontWeight, opacity: preset.opacity,
        straight: !!preset.straight, maxTextWidth: preset.maxTextWidth || 90
      };
    }

    _updatePreview() {
      if (!this._previewOverlay) return;

      const fullText = this.textInput?.value || '';
      const { line1, line2 } = this._splitText(fullText);
      const cssStack = this._getSelectedCssStack();
      const color = this.textColor || '#313131';
      const hasLine2 = line2.length > 0;
      const SVG_NS = 'http://www.w3.org/2000/svg';

      const eff = this._computeEffectiveParams(hasLine2);

      // Dynamic font size (same for both lines, based on longest line)
      const maxLen = Math.max(line1.length, line2.length || 0);
      let lineFontSize = this._calcDynamicFontSize(maxLen, eff.baseFontSize);

      const MAX_TEXT_WIDTH = eff.maxTextWidth;
      for (let attempt = 0; attempt < 5; attempt++) {
        const testWidths = this._measureCharWidthsForDisplay(line1 || line2, lineFontSize, cssStack || 'sans-serif');
        const totalW = testWidths.reduce((s, w) => s + w, 0) + Math.max(0, testWidths.length - 1) * eff.kerning;
        if (totalW <= MAX_TEXT_WIDTH) break;
        lineFontSize *= (MAX_TEXT_WIDTH / totalW);
      }

      const line1FontSize = lineFontSize;
      const line2FontSize = lineFontSize;

      let line1Chars, line2Chars;

      if (eff.straight) {
        line1Chars = this._placeCharsStraight(
          line1, line1FontSize, eff.kerning, eff.centerX, eff.centerY,
          hasLine2 ? -eff.nextLineOffset : 0
        );
        line2Chars = this._placeCharsStraight(
          line2, line2FontSize, eff.kerning, eff.centerX, eff.centerY,
          eff.nextLineOffset
        );
      } else {
        const baseR = eff.diameter / 2 - eff.baseFontSize / 2;
        const arcCenterY = eff.centerY + baseR;
        line1Chars = this._placeCharsOnArc(
          line1, line1FontSize, eff.diameter, eff.kerning,
          eff.centerX, arcCenterY,
          hasLine2 ? -eff.nextLineOffset : 0
        );
        line2Chars = this._placeCharsOnArc(
          line2, line2FontSize, eff.diameter, eff.kerning,
          eff.centerX, arcCenterY,
          eff.nextLineOffset
        );
      }

      const hasIcons = this._fontHasIcons(cssStack);
      const iconFallback = "'LD-ComicSans'";

      const makeCharEl = (ch, fontSize) => {
        const txt = document.createElementNS(SVG_NS, 'text');
        txt.setAttribute('x', '0');
        txt.setAttribute('y', '0');
        txt.setAttribute('transform', `translate(${ch.x},${ch.y}) rotate(${ch.rotation})`);
        txt.setAttribute('font-size', fontSize);
        txt.setAttribute('font-weight', eff.fontWeight || 500);
        txt.setAttribute('fill', color);
        txt.setAttribute('opacity', eff.opacity || 0.85);
        txt.setAttribute('text-anchor', 'middle');
        txt.setAttribute('dominant-baseline', 'central');
        if (ch.isIcon) {
          txt.setAttribute('font-family', hasIcons ? cssStack : iconFallback);
        } else {
          txt.setAttribute('font-family', cssStack || _SAFE_FONT);
        }
        txt.textContent = ch.char;
        return txt;
      };

      const allOverlays = this._getAllOverlays();

      allOverlays.forEach(overlay => {
        const g1 = overlay.querySelector('[data-arc-line1]');
        const g2 = overlay.querySelector('[data-arc-line2]');

        if (g1) {
          while (g1.firstChild) g1.removeChild(g1.firstChild);
          line1Chars.forEach(ch => g1.appendChild(makeCharEl(ch, line1FontSize)));
        }

        if (g2) {
          while (g2.firstChild) g2.removeChild(g2.firstChild);
          line2Chars.forEach(ch => g2.appendChild(makeCharEl(ch, line2FontSize)));
        }
      });

      if (line1 || line2) {
        this._showPreview();
      } else {
        this._hidePreview();
      }
    }

    _showPreview() {
      if (!this.toggle?.checked) return;
      if (!this._previewOverlay) return;

      if (!this._isOnFirstSlide()) {
        this._previewOverlay.style.display = 'none';
        return;
      }

      this._previewOverlay.style.display = 'block';
      this._previewOverlay.style.visibility = 'visible';
      this._previewOverlay.style.opacity = '1';
    }

    _hidePreview() {
      this._getAllOverlays().forEach(o => { o.style.display = 'none'; });
      if (this._previewOverlay) {
        this._previewOverlay.style.display = 'none';
      }
    }

    _getAllOverlays() {
      const overlays = [];
      // Always include the main overlay
      if (this._previewOverlay) {
        overlays.push(this._previewOverlay);
      }
      // Also find any cloned overlays in Swiper duplicated slides
      const section = document.getElementById(`MainProduct-${this.sectionId}`);
      if (section) {
        section.querySelectorAll('.personalization-preview-overlay').forEach(o => {
          if (!overlays.includes(o)) overlays.push(o);
        });
      }
      return overlays;
    }

    _observeMediaChanges() {
      // No longer needed - overlay is on gallery container, not individual slides
    }

    // ─── LocalStorage ───

    _saveToStorage() {
      try {
        const selectedOption = this.fontSelect?.options[this.fontSelect?.selectedIndex];
        const data = {
          text: this.textInput?.value || '',
          font: this.fontSelect?.value || '',
          cssStack: selectedOption?.dataset.cssStack || '',
          engravingOn: this.toggle?.checked || false,
          timestamp: Date.now()
        };
        localStorage.setItem(_STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        this._saveToCookie();
      }
    }

    _restoreFromStorage() {
      try {
        const raw = localStorage.getItem(_STORAGE_KEY);
        if (!raw) return;

        const data = JSON.parse(raw);

        // Check expiry
        if (Date.now() - data.timestamp > _STORAGE_EXPIRY) {
          localStorage.removeItem(_STORAGE_KEY);
          return;
        }

        // Restore values – handle both old format (text1/text2) and new (text)
        if (data.text && this.textInput) {
          this.textInput.value = data.text;
          this._handleTextInput();
        } else if (data.text1 && this.textInput) {
          // Migration from old two-field format
          const combined = (data.text1 || '') + (data.text2 || '');
          this.textInput.value = combined;
          this._handleTextInput();
        }

        if (data.font && this.fontSelect) {
          for (let i = 0; i < this.fontSelect.options.length; i++) {
            if (this.fontSelect.options[i].value === data.font) {
              this.fontSelect.selectedIndex = i;
              this.fontSelect.dispatchEvent(new Event('change', { bubbles: true }));
              break;
            }
          }
        }
        if (data.engravingOn && this.toggle) {
          this.toggle.checked = true;
          this.toggle.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } catch (e) {
        // Ignore storage read errors
      }
    }

    _saveToCookie() {
      try {
        const data = {
          t: this.textInput?.value || '',
          f: this.fontSelect?.value || ''
        };
        const expires = new Date(Date.now() + _STORAGE_EXPIRY).toUTCString();
        document.cookie = `${_STORAGE_KEY}=${encodeURIComponent(JSON.stringify(data))}; expires=${expires}; path=/; SameSite=Lax`;
      } catch (e) {
        // Silently fail
      }
    }

    // ─── Utilities ───

    _debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  customElements.define('product-personalization', ProductPersonalization);

  // Debug mode: run window.__engravingDebug() in console to check status
  window.__engravingDebug = function() {
    var el = document.querySelector('product-personalization');
    if (!el) { console.log('[Engraving] No <product-personalization> element found'); return; }
    console.log('[Engraving] Debug info:');
    console.log('  initialized:', el._initialized);
    console.log('  sectionId:', el.sectionId);
    console.log('  curveHandle:', el.curveHandle);
    console.log('  toggle checked:', el.toggle?.checked);
    console.log('  text value:', el.textInput?.value);
    console.log('  previewOverlay:', !!el._previewOverlay);
    console.log('  overlays in DOM:', document.querySelectorAll('.personalization-preview-overlay').length);
    console.log('  galleryViewer:', !!el._galleryViewer);
    var slides = document.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)');
    console.log('  slides found:', slides.length);
    slides.forEach(function(s,i) { console.log('    slide', i, '- has overlay:', !!s.querySelector('.personalization-preview-overlay')); });
  };
}